import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  BehaviorSubject,
  catchError,
  from,
  map,
  Observable,
  switchMap,
  take,
  tap,
  throwError,
  lastValueFrom,
} from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/angular';
import { Breadcrumb, BreadcrumbHint } from '@sentry/angular';
import { StorageService } from '../services/storage.service';
import { Router } from '@angular/router';
import { LoggerService } from '../services/logger.service';
import { DriverOnboardingStatus } from '../profile/model/profile.model';

interface LoginResponse {
  refreshToken: string;
  apexToken: string;
}

export enum UserRole {
  Manager = 'Manager',
  Driver = 'Driver',
  SingleDriver = 'Single Driver',
  ManagerDriver = 'Manager / Driver',
  FleetManager = 'customer',
}

export interface UserData {
  role: UserRole;
  email: string;
  id: string;
  firstName: string;
  lastName: string;
  language: string;
  refreshToken: string;
  onboardingStatus: DriverOnboardingStatus;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  authSubject$ = new BehaviorSubject(false);

  public role: UserRole;

  public isUserLoggedIn = false;

  public beforeAuthPage: boolean = false;
  public userData: UserData;

  /**
   * Public "before auth" pages a logged-out user is allowed to stay on
   * (e.g. the password-reset link opened from an email). Kept in sync with the
   * pages that set {@link beforeAuthPage} in their ngOnInit.
   */
  private static readonly BEFORE_AUTH_URL_SEGMENTS = [
    'forgot-password',
    'reset-password',
    'reset-success',
    'registration',
    'agb',
    'datenschutz',
    'impressum',
    'identity-check',
    'tracking/planned-transfer',
  ];

  public isAdmin = signal<boolean>(false);

  private logger: LoggerService = new LoggerService(AuthService.name);

  constructor(
    private httpClient: HttpClient,
    private storageService: StorageService,
    private router: Router,
  ) {
    this.isStillValid = this.isStillValid.bind(this);

    this.init();
  }

  async init() {
    const token = await this.storageService.get('ACCESS_TOKEN');

    if (token) {
      this.authSubject$.next(this.isStillValid(token));
    }

    setInterval(async () => {
      await this.storageService
        ?.get('ACCESS_TOKEN')
        ?.then(this.isStillValid)
        .then(async (valid) => {
          if (this.beforeAuthPage || this.isBeforeAuthUrl()) {
            this.logger.info({
              message: 'Before auth page. No need to authenticate.',
              identifier: 'movacarpro_auth_service_before_auth_page',
              method: 'init.setInterval.beforeAuthPage',
            });

            return;
          }

          if (!valid) {
            this.authSubject$.next(valid);

            this.logger.warn({
              message: 'Auth token is invalid. Navigating to login.',
              identifier: 'movacarpro_auth_service_token_is_invalid',
              method: 'init.setInterval.not.valid',
            });

            await this.storageService.delete('ACCESS_TOKEN');

            this.router.navigate(['/login']);
          }
        });
    }, 10000);
  }

  /**
   * Whether the live browser URL is a public "before auth" page. Checked
   * against `window.location` so the answer is available synchronously at
   * startup — unlike {@link beforeAuthPage}, which a lazy-loaded page only sets
   * once its ngOnInit has run, creating a startup race that could bounce a
   * logged-out user (e.g. on the reset-password link) to /login.
   */
  isBeforeAuthUrl(): boolean {
    const path = window.location?.pathname ?? '';

    return AuthService.BEFORE_AUTH_URL_SEGMENTS.some((segment) =>
      path.includes(segment),
    );
  }

  private isStillValid(token: string) {
    if (!token) {
      return false;
    }

    const decodedToken = jwtDecode(token) as { TTL?: number };
    const validUntil = decodedToken.TTL || Number.MAX_SAFE_INTEGER;
    return Date.now() < validUntil;
  }

  login(username: string, password: string) {
    const credentials = {
      username: username,
      password: password,
    };
    const url: string = `${environment.carmovaApiUrl}/login`;

    return this.httpClient.post<LoginResponse>(url, credentials).pipe(
      catchError((error: HttpErrorResponse) => {
        this.logger.warn({
          message: `Login for user ${username} failed: ${error.message}`,
          identifier: 'movacarpro_auth_service_login_failed',
          method: 'login.catchError',
        });

        return this.handleError(error, 'movacarpro_login_failed');
      }),
      take(1),
      switchMap((resData) => {
        const waitStorage = async () => {
          await this.storageService.set('ACCESS_TOKEN', resData.refreshToken);
          await this.storageService.set('APEX_TOKEN', resData.apexToken);

          return resData;
        };

        return from(waitStorage());
      }),
      switchMap(async (resData) => resData.refreshToken),
      tap((token: string) => {
        this.logger.info({
          message: `Login for user ${username} successful`,
          identifier: 'movacarpro_auth_service_login_success',
          method: 'login.tap',
        });

        this.authSubject$.next(this.isStillValid(token));
      }),
    );
  }

  resetPassword(username: string) {
    const credentials = {
      username: username,
    };
    const url = `${environment.carmovaApiUrl}/login/reset-password`;

    return this.httpClient
      .post(url, credentials)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.handleError(error, 'movacarpro_error_reset_password_failed'),
        ),
      );
  }

  async setNewPassword(password: string, rph: string): Promise<any> {
    const body = {
      resetPasswordHash: rph,
      password: password,
    };

    const url = `${environment.carmovaApiUrl}/login/set-new-password`;

    return await lastValueFrom(
      this.httpClient
        .post(url, body)
        .pipe(
          catchError((error: HttpErrorResponse) =>
            this.handleError(error, 'movacarpro_error_set_new_password_failed'),
          ),
        ),
    );
  }

  logout() {
    return from(
      (async () => {
        await this.storageService.clear();

        this.userData = {} as UserData;

        this.authSubject$.next(false);

        Sentry.setUser(null);
        this.isUserLoggedIn = false;

        this.logger.info({
          message: 'Logout completed successfully',
          identifier: 'movacarpro_auth_service_logout_success',
          method: 'logout',
        });
      })(),
    ).pipe(
      catchError((error) => {
        this.logger.error({
          message: `Logout failed: ${error.message}`,
          identifier: 'movacarpro_auth_service_logout_failed',
          method: 'logout',
        });

        return this.handleError(error, 'movacarpro_error_logout_failed');
      }),
    );
  }

  isLoggedIn() {
    return this.authSubject$.asObservable();
  }

  getToken(): Observable<string | undefined> {
    return from(
      (async () => {
        const token = await this.storageService.get('ACCESS_TOKEN');

        this.logger.info({
          message: token ? 'Token retrieved successfully' : 'No token found',
          identifier: token
            ? 'movacarpro_auth_service_token_found'
            : 'movacarpro_auth_service_no_token',
          method: 'getToken',
        });

        return token;
      })(),
    );
  }

  getUserData(): Observable<UserData | undefined> {
    return this.getToken().pipe(
      map((token) => {
        if (!token) {
          return undefined;
        }

        const userData: UserData = jwtDecode(token) as UserData;
        userData.language = userData.language ?? 'DE';

        Sentry.setUser({
          id: userData.id,
          email: userData.email,
        });

        this.userData = userData;

        return userData;
      }),
      catchError((error) =>
        this.handleError(error, 'movacarpro_error_load_user_data_failed'),
      ),
    );
  }

  getUserRole(): Observable<UserRole> {
    return this.getUserData().pipe(
      catchError((error) =>
        this.handleError(error, 'movacarpro_error_get_user_role_failed'),
      ),
      map((userData: UserData | undefined) => userData?.role as UserRole),
    );
  }

  private handleError(
    error: HttpErrorResponse,
    suggestedErrorMessage: string | undefined = undefined,
  ): Observable<never> {
    const errorMessagesToSupress: string[] = [
      'movacarpro_login_failed',
      'movacarpro_error_reset_password_failed',
      'movacarpro_error_logout_failed',
      'movacarpro_error_load_user_data_failed',
      'movacarpro_error_get_user_role_failed',
    ];

    const errorMessage: string =
      suggestedErrorMessage || 'movacarpro_error_message_unknown';

    // A client-side or network error occurred. Handle it as a Sentry Breadcrumb.
    if (error.status === 0) {
      return this.handleClientSideNetworkError(error);
    }

    if (errorMessagesToSupress.includes(errorMessage)) {
      return throwError(() => new Error(errorMessage));
    }

    // The backend returned an unsuccessful response code.
    // The response body may contain clues as to what went wrong.
    Sentry.captureException(error);
    return throwError(() => new Error(errorMessage));
  }

  private handleClientSideNetworkError(
    error: HttpErrorResponse,
  ): Observable<never> {
    const breadCrumb: Breadcrumb = {
      message: error.error.message,
      level: 'info',
      data: {
        errorJson: JSON.stringify(error),
      },
    } as Breadcrumb;

    const hint: BreadcrumbHint = {
      knownCauses: ['A client-side or network error occurred'],
    } as BreadcrumbHint;

    Sentry.addBreadcrumb(breadCrumb, hint);
    return throwError(
      () => new Error('movacarpro_error_client_side_network_error'),
    );
  }

  public setUnauthenticated(): void {
    this.authSubject$.next(false);
    this.isUserLoggedIn = false;
  }
}
